// Single Source of Truth: backend /api/public-config at runtime (no .env fallbacks)
import { apiRequest } from "../utils/apiClient";
let runtimePublicConfig = null; // { tenantId, clientId, apiAppIdUri }
let configPromise = null; // in-flight request, gedeeld door gelijktijdige callers

export async function loadAuthConfig() {
  if (runtimePublicConfig) return runtimePublicConfig;
  // Een tweede mount (StrictMode) deelt de lopende request in plaats van een
  // concurrerende call te starten die de eerste laat afbreken (ERR_ABORTED).
  if (configPromise) return configPromise;
  configPromise = (async () => {
    try {
      const res = await apiRequest("public-config", {
        timeoutMs: 8000,
        retries: 2,
        suppressAuthRedirect: true,
      });
      // API returns standardized envelope: { success, data: { tenantId, clientId, apiAppIdUri }, error, meta }
      const envelope = res?.data;
      if (envelope && typeof envelope === "object" && "data" in envelope) {
        runtimePublicConfig = envelope.data;
      } else {
        // Fallback: if backend later returns raw object (no envelope)
        runtimePublicConfig = envelope || null;
      }
      if (!runtimePublicConfig || !runtimePublicConfig.tenantId) {
        // Surface helpful diagnostic in dev console
        console.warn("Public config loaded but tenantId missing. Check /api/public-config response shape.", envelope);
      }
      return runtimePublicConfig;
    } catch (err) {
      // Bewust niet meer stil falen: een mislukte config-load leverde eerder een
      // eeuwige spinner zonder tekst of uitweg. De caller toont nu een fout met retry.
      configPromise = null;
      runtimePublicConfig = null;
      throw err;
    }
  })();
  return configPromise;
}

// Laat een expliciete retry de gecachete config/promise weggooien.
export function resetAuthConfig() {
  configPromise = null;
  runtimePublicConfig = null;
}

function getPublicConfig() {
  if (!runtimePublicConfig) {
    throw new Error("Public config not loaded from backend. Ensure /api/public-config is reachable.");
  }
  const redirectUri =
    typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : "http://localhost:3000";
  return { ...runtimePublicConfig, redirectUri };
}

// Non-throwing accessor for non-auth config (e.g. db environment). Returns null if not yet loaded.
export function getRuntimeConfig() {
  return runtimePublicConfig || null;
}

export function getMsalConfig() {
  const cfg = getPublicConfig();
  return {
    auth: {
      clientId: cfg.clientId,
      authority: `https://login.microsoftonline.com/${cfg.tenantId}`,
      redirectUri: cfg.redirectUri,
      postLogoutRedirectUri: cfg.redirectUri,
      navigateToLoginRequestUrl: true,
    },
    cache: {
      cacheLocation: "sessionStorage",
      storeAuthStateInCookie: true,
    },
  };
}

export function getLoginRequest() {
  const cfg = getPublicConfig();
  const base = (cfg.apiAppIdUri || "").trim();
  let resourceScope = null;
  if (base) {
    // Accept forms: api://<guid> or api://<any-valid-name-guid?> but must start with api:// to be valid for resource scope
    if (/^api:\/\/.+/.test(base)) {
      resourceScope = `${base.replace(/\/$/, "")}/access_as_user`;
    }
  }
  const scopes = ["openid", "profile", "email"]; // OIDC first
  if (resourceScope) scopes.push(resourceScope);
  return { scopes };
}
// No eager exports to avoid evaluating before runtime config is loaded
